import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { Button, Input, type Column } from "@legal-500/legal500-design-system";
import { DataTable } from "@/components/DataTable/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { usePermissions } from "@/hooks/usePermissions";
import {
  lawyerProfilesService,
  firmProfilesService,
  firmTabsService,
  articlesService,
  teamPagesService,
} from "@/api/profiles";
import { firms, type Firm } from "@/api/firms.service";
import styles from "./FirmInspector.module.css";

type FirmSummary = {
  companyId: string;
  counts: Record<string, number>;
  total: number;
};

const CONTENT_TYPES = [
  { key: "lawyerProfiles", label: "Lawyer Profiles", service: lawyerProfilesService },
  { key: "firmProfiles", label: "Firm Profiles", service: firmProfilesService },
  { key: "firmTabs", label: "Firm Tabs", service: firmTabsService },
  { key: "articles", label: "Articles", service: articlesService },
  { key: "teamPages", label: "Team Pages", service: teamPagesService },
] as const;

/**
 * Row shape for the shared <DataTable>. `key` is the stable identity for
 * `rowKey` (the content-type key, or "total" for the summary row). `isTotal`
 * drives the bold/Total-row rendering that the raw <table> previously expressed
 * with `styles.totalRow` + <strong>.
 */
type CountRow = {
  key: string;
  label: string;
  count: number;
  isTotal: boolean;
};

function buildCountRows(summary: FirmSummary): CountRow[] {
  const typeRows: CountRow[] = CONTENT_TYPES.map(ct => ({
    key: ct.key,
    label: ct.label,
    count: summary.counts[ct.key] ?? 0,
    isTotal: false,
  }));

  return [...typeRows, { key: "total", label: "Total", count: summary.total, isTotal: true }];
}

// Columns for the shared <DataTable>, preserving the original Content Type /
// Count columns. The Total row renders its cells with <strong> to match the
// previous emphasised summary row.
const countColumns: Column<CountRow>[] = [
  {
    header: "Content Type",
    accessor: "label",
    render: (_value, row) => (row.isTotal ? <strong>{row.label}</strong> : row.label),
  },
  {
    header: "Count",
    accessor: "count",
    render: (_value, row) => (row.isTotal ? <strong>{row.count}</strong> : row.count),
  },
];

const RECENT_FIRMS_KEY = "profiles_recent_firms";
const MAX_RECENT = 5;

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const DEBOUNCE_MS = 300;
const LISTBOX_ID = "firm-search-listbox";
const INPUT_ID = "firm-search-input";
const optionId = (index: number) => `firm-search-option-${index}`;

/**
 * Ordered option in the typeahead dropdown. The optional UUID row (when the
 * query is a valid UUID) is always first, followed by firm name matches.
 */
type SearchOption = { kind: "uuid"; value: string } | { kind: "firm"; firm: Firm };

function getRecentFirms(): string[] {
  try {
    const raw = sessionStorage.getItem(RECENT_FIRMS_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function addRecentFirm(companyId: string): void {
  const recent = getRecentFirms().filter(id => id !== companyId);
  recent.unshift(companyId);
  sessionStorage.setItem(RECENT_FIRMS_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}

export default function FirmInspector() {
  const { hasPermission } = usePermissions();
  const [searchInput, setSearchInput] = useState("");
  const [summary, setSummary] = useState<FirmSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recentFirms, setRecentFirms] = useState<string[]>([]);
  const [recentNames, setRecentNames] = useState<Map<string, string>>(new Map());

  // Typeahead state (mirrors CompanyPicker's debounce/open/loading/hasSearched
  // pattern, extended with keyboard-navigable activeIndex).
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [results, setResults] = useState<Firm[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  const trimmedQuery = searchInput.trim();
  const isUuid = UUID_RE.test(trimmedQuery);

  useEffect(() => {
    setRecentFirms(getRecentFirms());
  }, []);

  // Debounce the query.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchInput), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Firm name search. Skip when the query is empty or a UUID (a UUID string
  // won't match firm names — the dedicated UUID row handles that case).
  useEffect(() => {
    const query = debouncedQuery.trim();
    if (!query || UUID_RE.test(query)) {
      setResults([]);
      setHasSearched(false);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    firms
      .getAll({ search: query, limit: 10 })
      .then(resp => {
        if (!cancelled) {
          setResults(resp.companies);
          setActiveIndex(-1);
        }
      })
      .catch(err => {
        // Search failure shouldn't break the page — surface to the dev console
        // and show empty results rather than the page-level error banner.
        console.error("[FirmInspector] firm search failed", query, err);
        if (!cancelled) setResults([]);
      })
      .finally(() => {
        if (!cancelled) {
          setSearching(false);
          setHasSearched(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  // Resolve names for recently viewed firms via a single batch call.
  useEffect(() => {
    if (recentFirms.length === 0) {
      setRecentNames(new Map());
      return;
    }
    let cancelled = false;
    firms
      .getBatchByUuids(recentFirms)
      .then(companies => {
        if (cancelled) return;
        const map = new Map<string, string>();
        for (const company of companies) {
          if (company.id && company.name) map.set(company.id, company.name);
        }
        setRecentNames(map);
      })
      .catch(err => {
        // Name resolution is best-effort — fall back to rendering raw UUIDs.
        console.error("[FirmInspector] recent firm name resolution failed", err);
      });
    return () => {
      cancelled = true;
    };
  }, [recentFirms]);

  // Close the dropdown on outside click.
  useEffect(() => {
    function handle(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const options = useMemo<SearchOption[]>(() => {
    const opts: SearchOption[] = [];
    if (isUuid) opts.push({ kind: "uuid", value: trimmedQuery });
    for (const firm of results) opts.push({ kind: "firm", firm });
    return opts;
  }, [isUuid, trimmedQuery, results]);

  const handleSearch = useCallback(async (companyId: string) => {
    const trimmed = companyId.trim();
    if (!trimmed) return;

    setLoading(true);
    setError(null);
    setSummary(null);

    try {
      const searchResults = await Promise.allSettled(
        CONTENT_TYPES.map(ct =>
          ct.service.list({ companyId: trimmed, limit: 1, offset: 0 }).then(r => ({
            key: ct.key,
            total: r.total,
          })),
        ),
      );

      const counts: Record<string, number> = {};
      for (const result of searchResults) {
        if (result.status === "fulfilled") {
          counts[result.value.key] = result.value.total;
        }
      }

      const total = Object.values(counts).reduce((sum, v) => sum + v, 0);
      setSummary({ companyId: trimmed, counts, total });

      addRecentFirm(trimmed);
      setRecentFirms(getRecentFirms());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Search failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  const selectOption = useCallback(
    (option: SearchOption) => {
      setOpen(false);
      setActiveIndex(-1);
      if (option.kind === "uuid") {
        void handleSearch(option.value);
      } else {
        setSearchInput(option.firm.name);
        void handleSearch(option.firm.id);
      }
    },
    [handleSearch],
  );

  const canSubmit =
    trimmedQuery.length > 0 && (isUuid || (activeIndex >= 0 && activeIndex < options.length));

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (activeIndex >= 0 && options[activeIndex]) {
      selectOption(options[activeIndex]);
      return;
    }
    if (isUuid) {
      setOpen(false);
      void handleSearch(trimmedQuery);
    }
    // Partial name with no highlighted option: keep the dropdown open, do nothing.
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setOpen(true);
        setActiveIndex(prev => (options.length === 0 ? -1 : (prev + 1) % options.length));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex(prev =>
          options.length === 0 ? -1 : prev <= 0 ? options.length - 1 : prev - 1,
        );
        break;
      case "Enter":
        if (activeIndex >= 0 && options[activeIndex]) {
          e.preventDefault();
          selectOption(options[activeIndex]);
        }
        // Otherwise fall through to the form's submit handler.
        break;
      case "Escape":
        setOpen(false);
        setActiveIndex(-1);
        break;
      default:
        break;
    }
  };

  if (!hasPermission("profiles.read")) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.accessDenied} role="alert">
          <p>You do not have permission to access the Firm Inspector.</p>
        </div>
      </div>
    );
  }

  const showDropdown = open && (options.length > 0 || searching || hasSearched);

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title="Firm Inspector"
        subtitle="Search for a firm by name or company ID (UUID) to inspect all their profiles"
      />

      <section className={styles.searchSection} aria-labelledby="search-heading">
        <h2 id="search-heading" className={styles.srOnly}>
          Firm Search
        </h2>
        <form
          onSubmit={handleFormSubmit}
          className={styles.searchForm}
          role="search"
          aria-label="Search for firm by name or company ID"
        >
          <div className={styles.inputRow}>
            <div ref={containerRef} className={styles.comboboxContainer}>
              <Input
                id={INPUT_ID}
                label="Firm name or company ID (UUID)"
                type="text"
                value={searchInput}
                onChange={e => {
                  setSearchInput(e.target.value);
                  setOpen(true);
                  setActiveIndex(-1);
                }}
                onFocus={() => setOpen(true)}
                onKeyDown={handleKeyDown}
                placeholder="Start typing a firm name, or paste a company UUID"
                aria-describedby="companyIdHint"
                autoComplete="off"
                spellCheck={false}
                fullWidth
                helperText="Search by firm name, or enter a company UUID, to view all profiles for that firm."
                role="combobox"
                aria-expanded={showDropdown}
                aria-controls={LISTBOX_ID}
                aria-autocomplete="list"
                aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
              />
              {showDropdown && (
                <ul id={LISTBOX_ID} role="listbox" className={styles.comboboxList}>
                  {searching && <li className={styles.comboboxStatus}>Searching…</li>}
                  {!searching &&
                    options.map((option, index) => {
                      const isActive = index === activeIndex;
                      if (option.kind === "uuid") {
                        return (
                          <li
                            key={`uuid-${option.value}`}
                            id={optionId(index)}
                            role="option"
                            aria-selected={isActive}
                            className={styles.comboboxItem}
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => selectOption(option)}
                          >
                            <span className={styles.comboboxItemPrimary}>
                              Search UUID: {option.value}
                            </span>
                          </li>
                        );
                      }
                      return (
                        <li
                          key={option.firm.id}
                          id={optionId(index)}
                          role="option"
                          aria-selected={isActive}
                          className={styles.comboboxItem}
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => selectOption(option)}
                        >
                          <span className={styles.comboboxItemPrimary}>{option.firm.name}</span>
                          {(option.firm.firmRef || option.firm.companyId) && (
                            <span className={styles.comboboxItemMeta}>
                              {option.firm.firmRef ?? `Company ${option.firm.companyId}`}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  {!searching && hasSearched && options.length === 0 && (
                    <li className={styles.comboboxStatus} aria-live="polite">
                      No matching firms
                    </li>
                  )}
                </ul>
              )}
            </div>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading || !canSubmit}
              aria-label="Search for firm"
            >
              {loading ? "Searching..." : "Search"}
            </Button>
          </div>
        </form>
      </section>

      {error && (
        <div className={styles.errorBanner} role="alert">
          <p>{error}</p>
        </div>
      )}

      {summary && (
        <section className={styles.summarySection} aria-labelledby="summary-heading">
          <h2 id="summary-heading" className={styles.sectionHeading}>
            Results for firm: <span className={styles.companyIdDisplay}>{summary.companyId}</span>
          </h2>

          {summary.total === 0 ? (
            <p className={styles.emptyText}>No profiles found for this company ID.</p>
          ) : (
            <>
              <DataTable data={buildCountRows(summary)} columns={countColumns} rowKey="key" />

              <div className={styles.viewActions}>
                <Link
                  to={`/profiles/firms/${summary.companyId}`}
                  className={styles.viewButton}
                  aria-label={`View all profiles for firm ${summary.companyId}`}
                >
                  View Firm Profiles &rarr;
                </Link>
              </div>
            </>
          )}
        </section>
      )}

      {recentFirms.length > 0 && (
        <section className={styles.recentSection} aria-labelledby="recent-heading">
          <h2 id="recent-heading" className={styles.sectionHeading}>
            Recently Viewed Firms
          </h2>
          <ul className={styles.recentList} role="list">
            {recentFirms.map(firmId => {
              const name = recentNames.get(firmId);
              return (
                <li key={firmId} className={styles.recentItem}>
                  <button
                    type="button"
                    className={styles.recentIdButton}
                    onClick={() => {
                      setSearchInput(name ?? firmId);
                      setOpen(false);
                      void handleSearch(firmId);
                    }}
                    aria-label={`Search again for firm ${name ?? firmId}`}
                  >
                    <span className={styles.recentPrimary}>{name ?? firmId}</span>
                    <span className={styles.recentUuidMeta}>{firmId}</span>
                  </button>
                  <Link
                    to={`/profiles/firms/${firmId}`}
                    className={styles.recentLink}
                    aria-label={`View profiles for firm ${name ?? firmId}`}
                  >
                    View
                  </Link>
                </li>
              );
            })}
            {/* `recentIdButton` is a styled-link button — it's not a form input,
                so we keep it as a native button with token-driven styling. */}
          </ul>
        </section>
      )}
    </div>
  );
}
